import type { CurrentSessionState } from './useCurrentSession';

/**
 * 現セッション選択 UI (user 第 2 弾、Phase 5)。
 *
 * 旧 v1 の Onboarding 経路 (URL `/m/:token/:sessionId`) は Phase 5 後続で再移植。
 * 本 minimum 版は AppHome に inline 配置し、active session 一覧から select 1 件で切替。
 * 「未選択 = 全セッション (filter 効かず、Announcement.targetSessionIds 非空時は弾かれる)」。
 *
 * state は caller が `useCurrentSession` で 1 度だけ取得して props 経由で渡す
 * (component 内部で再度 hook を呼ぶと AppHome の state と分離し、選択変更が Banner/Modal/PostForm
 * に伝播しない UX バグになるため。user 第 2 弾 reviewer 指摘済)。
 *
 * **UI3 AC 補足 (docs/16 §10-2 P2 H3、Sprint AN)** —
 *   本 component は **view filter** 相当 (= 「どのセッションでフィルタするか」の表示制御) のため、
 *   PostFilterBar の checkbox / date range と同様に「即時反映」で OK。
 *   永続化先 (localStorage `cw.currentSessionId:{districtId}`) は他の view filter (date / category)
 *   とは異なり追加で localStorage に保存しているが、これは「ブラウザ再起動後も最後に選んでいた
 *   セッションを保つ」UX 要件 (REQ-4.1.20 + 旧 v1 Onboarding 経路の Phase 5 minimum 代替) のため。
 *   将来 SettingsModal 内に「参加セッション切替」 (membership 変更) を追加する場合は、それは
 *   view filter ではなく Firestore membership の書込みなので **Save button pattern (UI3 統一)** に従う。
 */
export interface SessionPickerProps {
  state: CurrentSessionState;
  onChange: (id: string | null) => void;
}

export function SessionPicker({ state, onChange }: SessionPickerProps) {
  if (state.status === 'loading') {
    return <p className="text-xs text-slate-500">セッション読込中…</p>;
  }
  if (state.status === 'error') {
    return <p className="text-xs text-danger-600">セッション読込エラー: {state.error.message}</p>;
  }
  if (state.items.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        アクティブなセッションがありません (admin が登録すると表示されます)。
      </p>
    );
  }

  return (
    <label className="flex items-center gap-2 text-xs text-slate-600">
      <span>セッション:</span>
      <select
        value={state.currentSessionId ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="min-h-touch px-2 border border-slate-300 rounded bg-white text-sm flex-1"
        aria-label="現在のセッションを選択"
      >
        <option value="">(未選択)</option>
        {state.items.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.code})
          </option>
        ))}
      </select>
    </label>
  );
}
